// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { HasShellContext } from "@insite/client-framework/Components/IsInShell";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { GridContainerProps } from "@insite/mobius/GridContainer";
import { GridItemProps } from "@insite/mobius/GridItem";
import Hidden, { HiddenProps } from "@insite/mobius/Hidden";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import XngZone from "@tps/App/Components/Common/XngZone";
import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

type Props = WidgetProps & HasShellContext;

export interface ProductDetailsViewStyles {
    container?: GridContainerProps;
    leftColumnGridItem?: GridItemProps;
    leftColumnInnerContainer?: GridContainerProps;
    topGridItem?: GridItemProps;
    middleGridItem?: GridItemProps;
    middleGridItemHidden?: HiddenProps;
    bottomGridItem?: GridItemProps;
    rightColumnGridItem?: GridItemProps;
    rightColumnGridItemHidden?: HiddenProps;
    // XNG-Changes
    wrapper?: InjectableCss;
    leftSection?: InjectableCss;
    mainSection?: InjectableCss;
    rightSection?: InjectableCss;
    imagesSection?: InjectableCss;
    productTitleHiddenMobile?: InjectableCss;
    productTitleHidden?: InjectableCss;
    productCarouselsMobile?: InjectableCss;
    productCarouselDesktop?: InjectableCss;
    badgesWrapper?: InjectableCss;
}

export const viewStyles: ProductDetailsViewStyles = {
    leftColumnGridItem: {
        width: [12, 12, 8, 8, 8],
        css: css`
            overflow: hidden;
            direction: ltr;
        `,
    },
    topGridItem: { width: 12 },
    middleGridItem: { width: [12, 12, 0, 0, 0] },
    middleGridItemHidden: {
        above: "sm",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    bottomGridItem: { width: 12 },
    rightColumnGridItem: { width: [0, 0, 4, 4, 4] },
    rightColumnGridItemHidden: {
        below: "md",
        css: css`
            width: 100%;
            direction: ltr;
        `,
    },
    container: {
        css: css`
            direction: rtl;
        `,
    },
};

const styles = viewStyles;

// XNG-Change: change structure to match design
const ProductDetailsView: React.FC<Props> = props => {
    return (
        <>
            <StyledWrapper {...styles.wrapper} data-test-selector="productDetailsView">
                <StyledWrapper {...styles.leftSection}>
                    <Hidden above="md" {...styles.productTitleHiddenMobile}>
                        <XngZone contentId={props.id} zoneName="Content0" hint="Zone For Product Details Title" />
                        <StyledWrapper {...styles.badgesWrapper}>
                            <XngZone
                                contentId={props.id}
                                normalWidth={true}
                                zoneName="Content1"
                                hint="Zone For Product Details Badges"
                            />
                        </StyledWrapper>
                    </Hidden>
                    <StyledWrapper {...styles.imagesSection}>
                        <XngZone contentId={props.id} zoneName="Content2" hint="Zone For Image" />
                        <XngZone contentId={props.id} zoneName="Content3" hint="Zone For Product Share" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.mainSection}>
                        <Hidden below="lg" {...styles.productTitleHidden}>
                            <XngZone contentId={props.id} zoneName="Content0" hint="Zone For Product Details Title" />
                            <StyledWrapper {...styles.badgesWrapper}>
                                <XngZone
                                    contentId={props.id}
                                    normalWidth={true}
                                    zoneName="Content1"
                                    hint="Zone For Product Details Badges"
                                />
                            </StyledWrapper>
                        </Hidden>
                        <XngZone contentId={props.id} zoneName="Content4" hint="Zone For Product Attributes" />
                    </StyledWrapper>
                    <StyledWrapper {...styles.productCarouselDesktop}>
                        <XngZone contentId={props.id} zoneName="Content6" hint="Zone For Product Carousels" />
                    </StyledWrapper>
                </StyledWrapper>
                <StyledWrapper {...styles.rightSection}>
                    <XngZone contentId={props.id} zoneName="Content5" hint="Zone For Product Variants" />
                </StyledWrapper>
                <StyledWrapper {...styles.productCarouselsMobile}>
                    <XngZone contentId={props.id} zoneName="Content6" hint="Zone For Product Carousels" />
                </StyledWrapper>
            </StyledWrapper>
        </>
    );
};

const widgetModule: WidgetModule = {
    component: connect()(ProductDetailsView),
    definition: {
        displayName: "View",
        group: "Product Details",
        allowedContexts: ["ProductDetailsPage"],
    },
};

export default widgetModule;
